import classNames from "classnames"
import React, { useEffect, useState } from "react"
import { FunctionComponent, ReactNode } from "react"

interface IProps {
  children: ReactNode
  id?: string
  shouldFlip?: boolean
}

enum FlipMode {
  Front = "front",
  Back = "back"
}

/**
 * this component wraps two element into the option to be flipped between them on mouseover or on click
 *
 * NOTE: known issues: ARIA:
 * Untreated, the elements in the back card or front card are accessible by keyboard, even if the card is not to be seen
 * This is unexpected for users using keyboard navigation.
 * Therefor the FlippingElement adds a boolean attribute accessibleByKeyboard to the child elements (front + back),
 * depending on what element is to be seen.
 * The child should set/unset tabIndex in those elements that are possibly accessible by keyboard navigation:
 * links + buttons, accessible other elements. They should only be accessible if the element is shown.
 */
const FlippingElement: FunctionComponent<IProps> = (props: IProps) => {
  const { children, id, shouldFlip = true } = props
  const childrenArray = React.Children.toArray(children) as unknown as React.ReactElement<any>[]

  const reallyShouldFlip = shouldFlip && !!childrenArray[1]

  const [flipMode, setFlipMode] = useState<FlipMode>(FlipMode.Front)
  const toggleFlipMode = () => flipMode === FlipMode.Front && reallyShouldFlip ? setFlipMode(FlipMode.Back) : setFlipMode(FlipMode.Front)

  useEffect(() => {
    // Make sure the front card is always shown if the elements should not flip:
    // explicitely b/c of async processes.
    if (!reallyShouldFlip) {
      setFlipMode(FlipMode.Front)
    }
  }, [reallyShouldFlip])

  const onEnter = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter" && reallyShouldFlip) {
      toggleFlipMode()
      // the event needs to bubble to allow (keyboard) clicking on links!
      // -> no: event.preventDefault()
    }
  }

  // wrap children into a div for performing the flips
  const frontChild = childrenArray[0]
    ? <div
      className="flipping-container__front-element"
    >
      {React.cloneElement(childrenArray[0], { accessibleByKeyboard: flipMode === FlipMode.Front })}
    </div>
    : <></>

  const backChild = childrenArray[1]
    ? <div
      className="flipping-container__back-element"
    >
      {React.cloneElement(childrenArray[1], { accessibleByKeyboard: flipMode === FlipMode.Back })}
    </div>
    : null

  return <div
    className={classNames(
      "flipping-container",
      { "clickable": reallyShouldFlip },
      { "flipping-container--flipped": flipMode === FlipMode.Back },
      { "flipping-container--flipped-to-front": reallyShouldFlip && flipMode === FlipMode.Front }
    )}
    id={id}
    tabIndex={reallyShouldFlip ? 0 : -1} // accessible by keyboard only, if the card reallyShouldFlip
    onClick={reallyShouldFlip ? toggleFlipMode : null}
    onKeyDown={reallyShouldFlip ? onEnter : null}
  >
    <div className="flipping-container__inner">
      {frontChild}
      {// do not show the back element when it should not be flipped to at all
        reallyShouldFlip && backChild
      }
    </div>
  </div>
}

export default FlippingElement
